import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import { ComboboxAutocomplete } from '../../../components/Form/ComboboxAutocomplete'
import type { Utility } from '../../../apis/types'
import { createTariffApi } from '../../../apis/apis'
import { FieldTooltip } from './TariffFieldKey'
import { UTILITY_TYPE_HINTS, type UtilityTypeFilter } from './TariffFilterBar'

const inputClass =
    'block w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100'

const TYPE_OPTIONS: Array<{ value: UtilityTypeFilter; label: string; tooltip: string }> = [
    { value: 'IOU', label: 'IOU', tooltip: UTILITY_TYPE_HINTS.IOU },
    { value: 'CCA', label: 'CCA', tooltip: UTILITY_TYPE_HINTS.CCA },
    { value: 'ESP_DA', label: 'DA', tooltip: UTILITY_TYPE_HINTS.ESP_DA },
]

type FieldErrors = {
    utilityType?: string
    utilityCuid?: string
    schedule?: string
}

export const CreateTariffModal: React.FC<{
    open: boolean
    onClose: () => void
    iouUtilities: Utility[]
    ccaUtilities: Utility[]
    daUtilities: Utility[]
    onCreateUtility?: () => void
    preselectUtility?: Utility | null
}> = ({ open, onClose, iouUtilities, ccaUtilities, daUtilities, onCreateUtility, preselectUtility }) => {
    const navigate = useNavigate()
    const [utilityType, setUtilityType] = useState<UtilityTypeFilter>('ESP_DA')
    const [utilityCuid, setUtilityCuid] = useState('')
    const [schedule, setSchedule] = useState('')
    const [name, setName] = useState('')
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

    const utilities = useMemo(() => {
        if (utilityType === 'IOU') return iouUtilities
        if (utilityType === 'CCA') return ccaUtilities
        if (utilityType === 'ESP_DA') return daUtilities
        return [...iouUtilities, ...ccaUtilities, ...daUtilities]
    }, [utilityType, iouUtilities, ccaUtilities, daUtilities])

    const utilityOptions = useMemo(
        () =>
            utilities.map((u) => ({
                id: u.id,
                name:
                    u.shortName != null && u.shortName !== ''
                        ? `${u.shortName} — ${u.name}`
                        : u.name,
            })),
        [utilities]
    )

    const preselectType =
        preselectUtility == null
            ? null
            : preselectUtility.type === 'CCA' || preselectUtility.type === 'ESP_DA'
              ? preselectUtility.type
              : 'IOU'

    useEffect(() => {
        if (preselectType != null) {
            setUtilityType(preselectType)
        }
    }, [preselectType])

    useEffect(() => {
        if (preselectUtility != null && utilityType === preselectType) {
            setUtilityCuid(preselectUtility.id)
            return
        }
        // Clear utility when type changes so the filtered list cannot keep a stale pick.
        setUtilityCuid('')
    }, [utilityType, preselectType, preselectUtility])

    const resetAndClose = (): void => {
        setUtilityType('ESP_DA')
        setUtilityCuid('')
        setSchedule('')
        setName('')
        setError(null)
        setFieldErrors({})
        onClose()
    }

    const validate = (): boolean => {
        const next: FieldErrors = {}
        if (utilityType === 'ALL') next.utilityType = 'Select a utility type.'
        if (utilityCuid === '') next.utilityCuid = 'Utility is required.'
        if (schedule.trim() === '') next.schedule = 'Schedule is required.'
        setFieldErrors(next)
        return Object.keys(next).length === 0
    }

    const handleCreate = (): void => {
        if (!validate()) return
        setSaving(true)
        setError(null)
        void createTariffApi({
            utilityCuid,
            schedule: schedule.trim(),
            name: name.trim() === '' ? null : name.trim(),
            status: 'PENDING',
        })
            .then((created) => {
                resetAndClose()
                navigate(`/admin/tariffs/${created.cuid}`)
            })
            .catch(() => setError('Failed to create tariff.'))
            .finally(() => setSaving(false))
    }

    return (
        <Transition show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={resetAndClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50 dark:bg-black/70" />
                </Transition.Child>
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-4 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-5 text-left shadow-xl dark:bg-slate-900">
                                <Dialog.Title className="text-lg font-medium text-slate-900 dark:text-slate-100">
                                    Create tariff
                                </Dialog.Title>
                                <div
                                    className="mt-3 flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-100"
                                    role="status"
                                >
                                    <InformationCircleIcon
                                        className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400"
                                        aria-hidden="true"
                                    />
                                    <p>
                                        This tariff will be created as Pending. Add rates
                                        on the detail page, then activate when it is ready for
                                        assignment.
                                    </p>
                                </div>
                                <div className="mt-4 space-y-3">
                                    <div>
                                        <div className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Utility type
                                        </div>
                                        <div className="inline-flex rounded-md shadow-sm" role="group" aria-label="Utility type">
                                            {TYPE_OPTIONS.map((opt, index) => (
                                                <FieldTooltip key={opt.value} content={opt.tooltip}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setUtilityType(opt.value)}
                                                        className={[
                                                            'border px-3 py-1.5 text-sm font-medium focus:z-10 focus:outline-none focus:ring-2 focus:ring-indigo-500',
                                                            index === 0 ? 'rounded-l-md' : '-ml-px',
                                                            index === TYPE_OPTIONS.length - 1 ? 'rounded-r-md' : '',
                                                            utilityType === opt.value
                                                                ? 'border-indigo-600 bg-indigo-600 text-white'
                                                                : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200',
                                                        ].join(' ')}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                </FieldTooltip>
                                            ))}
                                        </div>
                                        {fieldErrors.utilityType != null ? (
                                            <p className="mt-1 text-xs text-red-600">{fieldErrors.utilityType}</p>
                                        ) : null}
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Utility
                                        </label>
                                        <ComboboxAutocomplete
                                            labelPlural="Utilities"
                                            value={utilityCuid}
                                            handleChange={(id) =>
                                                setUtilityCuid(id != null ? String(id) : '')
                                            }
                                            data={utilityOptions}
                                        />
                                        {fieldErrors.utilityCuid != null ? (
                                            <p className="mt-1 text-xs text-red-600">{fieldErrors.utilityCuid}</p>
                                        ) : null}
                                        {onCreateUtility != null ? (
                                            <button
                                                type="button"
                                                className="mt-1 text-xs text-indigo-600 hover:underline dark:text-indigo-400"
                                                onClick={onCreateUtility}
                                            >
                                                Utility not listed? Create one
                                            </button>
                                        ) : null}
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Schedule
                                        </label>
                                        <input
                                            className={inputClass}
                                            value={schedule}
                                            onChange={(e) => setSchedule(e.target.value)}
                                            placeholder={
                                                utilityType === 'ESP_DA' ? 'e.g. ESP_DA-FP-1' : 'e.g. B-19'
                                            }
                                        />
                                        {utilityType === 'ESP_DA' ? (
                                            <p className="mt-1 text-xs text-slate-500">
                                                Direct Access schedules are typically prefixed with ESP_DA.
                                            </p>
                                        ) : null}
                                        {fieldErrors.schedule != null ? (
                                            <p className="mt-1 text-xs text-red-600">{fieldErrors.schedule}</p>
                                        ) : null}
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Name
                                        </label>
                                        <input
                                            className={inputClass}
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                    </div>
                                    {error != null ? (
                                        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                                    ) : null}
                                </div>
                                <div className="mt-5 flex justify-end gap-2">
                                    <button
                                        type="button"
                                        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                                        onClick={resetAndClose}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60"
                                        onClick={handleCreate}
                                        disabled={saving}
                                    >
                                        {saving ? 'Creating…' : 'Create'}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}

export default CreateTariffModal
